import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "@/components/ui/sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Sparkles,
  ArrowRight,
  ArrowLeft,
  Check,
  Loader2,
  MessageSquare,
  Wand2,
  Palette,
  Users,
  Link as LinkIcon,
  RefreshCw,
  Plus,
  X,
  CheckCircle2,
  HelpCircle,
  Zap,
  BookMarked,
  Building2
} from "lucide-react";
import { BrandLogoPickerDialog, BrandLogoImg } from "@/components/BrandLogoPickerDialog";
import { useBrandDetection } from "@/hooks/useBrandDetection";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Step indicator component
const StepIndicator = ({ currentStep, totalSteps, steps }) => {
  return (
    <div className="flex items-center justify-center gap-2 mb-8">
      {steps.map((step, index) => (
        <React.Fragment key={index}>
          <div className={`flex items-center gap-2 ${index <= currentStep ? 'text-primary' : 'text-muted-foreground'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium border-2 transition-colors
              ${index < currentStep ? 'bg-primary text-primary-foreground border-primary' : ''}
              ${index === currentStep ? 'border-primary text-primary' : ''}
              ${index > currentStep ? 'border-muted-foreground/30' : ''}
            `}>
              {index < currentStep ? <Check className="w-4 h-4" /> : index + 1}
            </div>
            <span className={`hidden md:inline text-sm ${index === currentStep ? 'font-medium' : ''}`}>
              {step}
            </span>
          </div>
          {index < totalSteps - 1 && (
            <div className={`w-8 h-0.5 ${index < currentStep ? 'bg-primary' : 'bg-muted-foreground/30'}`} />
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

/**
 * BrandLibrarySection — curated brand logos (Claude, ChatGPT, GitHub, etc.)
 * with live auto-detection banner when matching brand names appear in the
 * video description.
 */
const BrandLibrarySection = ({
  selectedSlugs,
  onChange,
  onOpenPicker,
  videoDescription,
  dismissed,
  onDismissSuggestion,
}) => {
  const { matches, suggestedNewSlugs } = useBrandDetection(videoDescription, selectedSlugs);
  const showBanner = !dismissed && suggestedNewSlugs.length > 0;
  const suggestedBrands = matches.filter((m) => suggestedNewSlugs.includes(m.slug));

  return (
    <div className="rounded-lg border bg-muted/20 p-4 space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Building2 className="w-4 h-4 text-primary" />
          <h4 className="text-sm font-semibold">Marken-Bibliothek</h4>
          {selectedSlugs.length > 0 && (
            <Badge variant="secondary">{selectedSlugs.length} ausgewählt</Badge>
          )}
        </div>
        <Button variant="outline" size="sm" onClick={onOpenPicker} data-testid="open-brand-picker">
          <Plus className="w-3.5 h-3.5 mr-1" />
          Marken auswählen
        </Button>
      </div>

      {showBanner && (
        <div
          className="flex items-start gap-3 rounded-md border border-primary/30 bg-primary/5 px-3 py-2"
          data-testid="brand-suggestion-banner"
        >
          <Sparkles className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
          <div className="flex-1 text-xs">
            <p className="font-medium mb-1.5">
              Wir haben passende Logos in deiner Beschreibung gefunden:
            </p>
            <div className="flex flex-wrap gap-1.5">
              {suggestedBrands.map((b) => (
                <button
                  key={b.slug}
                  type="button"
                  onClick={() => onChange([...selectedSlugs, b.slug])}
                  className="inline-flex items-center gap-1 rounded-full bg-white dark:bg-zinc-800 border border-primary/30 px-2 py-0.5 text-[11px] hover:bg-primary/10 transition-colors"
                  data-testid={`brand-suggest-${b.slug}`}
                >
                  <span className="w-3.5 h-3.5 inline-block">
                    <BrandLogoImg slug={b.slug} name={b.name} size="w-3.5 h-3.5" />
                  </span>
                  ➕ {b.name}
                </button>
              ))}
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 flex-shrink-0"
            onClick={onDismissSuggestion}
            data-testid="brand-suggest-dismiss"
          >
            <X className="w-3.5 h-3.5" />
          </Button>
        </div>
      )}

      {selectedSlugs.length === 0 ? (
        <p className="text-xs text-muted-foreground italic">
          Noch keine Marken ausgewählt. Klicke oben oder schreibe „Claude", „ChatGPT", „GitHub" etc. in die Beschreibung.
        </p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {selectedSlugs.map((slug) => {
            const meta = matches.find((m) => m.slug === slug);
            return (
              <div
                key={slug}
                className="flex items-center gap-1.5 rounded-md border bg-card pl-1 pr-2 py-1"
                data-testid={`brand-chip-${slug}`}
              >
                <div className="w-5 h-5">
                  <BrandLogoImg slug={slug} name={meta?.name || slug} size="w-5 h-5" />
                </div>
                <span className="text-xs">{meta?.name || slug}</span>
                <button
                  type="button"
                  onClick={() => onChange(selectedSlugs.filter((s) => s !== slug))}
                  className="text-muted-foreground hover:text-destructive"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export const Generator2Page = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Wizard state
  const [currentStep, setCurrentStep] = useState(0);
  const [generatorMode, setGeneratorMode] = useState('manual'); // 'manual' or 'video-url'
  
  // Thumbnail templates
  const [thumbnailTemplates, setThumbnailTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  
  // Session state
  const [sessionId, setSessionId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  
  // Step 1: Video description
  const [videoDescription, setVideoDescription] = useState('');
  const [videoUrl, setVideoUrl] = useState('');
  const [videoMetadata, setVideoMetadata] = useState(null);
  
  // Step 2: Questions
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  
  // Step 3: Title selection
  const [titleSuggestions, setTitleSuggestions] = useState([]);
  const [selectedTitle, setSelectedTitle] = useState('');
  const [customTitle, setCustomTitle] = useState(''); // NEW: Manual title input
  
  // Step 4: Style selection
  const [styles, setStyles] = useState([]);
  const [selectedStyleId, setSelectedStyleId] = useState('');
  
  // Step 5: Brand colors
  const [brandColors, setBrandColors] = useState([]);
  const [newBrandColor, setNewBrandColor] = useState('#');
  const [colorSourceStyleId, setColorSourceStyleId] = useState(''); // Style for color palette
  
  // Step 6: Tone/Case
  const [toneCases, setToneCases] = useState([]);
  const [selectedToneCase, setSelectedToneCase] = useState('ai_decides');
  
  // Step 7: Persons
  const [persons, setPersons] = useState([]);
  const [selectedPersons, setSelectedPersons] = useState([]);
  
  // Step 8: Logos
  const [logos, setLogos] = useState([]);
  const [selectedLogos, setSelectedLogos] = useState([]);
  // Brand logos from the curated system library (Claude, ChatGPT, GitHub, etc.)
  const [selectedBrandSlugs, setSelectedBrandSlugs] = useState([]);
  const [brandPickerOpen, setBrandPickerOpen] = useState(false);
  const [dismissedBrandSuggestion, setDismissedBrandSuggestion] = useState(false);
  const [logoPosition, setLogoPosition] = useState('ai_decides'); // 'ai_decides', 'top-left', 'top-right', 'bottom-left', 'bottom-right', 'center'
  
  // Step 9: Variations & Generate
  const [variationsCount, setVariationsCount] = useState(4);
  const [thumbnailFormat, setThumbnailFormat] = useState('youtube');
  const [imageInstructions, setImageInstructions] = useState('');
  
  const steps = [
    'Video beschreiben',
    'Fragen beantworten',
    'Titel wählen',
    'Style',
    'Farben',
    'Ton',
    'Personen',
    'Logos',
    'Generieren'
  ];
  
  // Load initial data
  useEffect(() => {
    const loadData = async () => {
      // Load each data source separately to be resilient to individual failures
      try {
        const stylesRes = await axios.get(`${API}/styles`);
        const completedStyles = stylesRes.data.filter(s => s.status === 'completed');
        if (completedStyles.length > 0) {
          setStyles(completedStyles);
          setSelectedStyleId(prev => prev || completedStyles[0].id);
        }
      } catch (err) {
        console.error('Error loading styles:', err);
        // Keep existing data on error
      }
      
      try {
        const toneRes = await axios.get(`${API}/generator/tone-cases`);
        if (toneRes.data.tone_cases?.length > 0) {
          setToneCases(toneRes.data.tone_cases);
        }
      } catch (err) {
        console.error('Error loading tone cases:', err);
      }
      
      try {
        const personsRes = await axios.get(`${API}/persons`);
        if (personsRes.data?.length > 0) {
          setPersons(personsRes.data);
        }
      } catch (err) {
        console.error('Error loading persons:', err);
      }
      
      try {
        const logosRes = await axios.get(`${API}/logos`);
        if (logosRes.data?.length > 0 || logosRes.data?.logos?.length > 0) {
          setLogos(logosRes.data.logos || logosRes.data || []);
        }
      } catch (err) { /* keep existing */ }

      try {
        const tplRes = await axios.get(`${API}/thumbnail-templates`);
        if (tplRes.data?.length > 0) setThumbnailTemplates(tplRes.data);
      } catch (err) { /* no templates yet */ }

      // Reuse from job
      const reuseJobId = searchParams.get("reuse");
      if (reuseJobId) {
        try {
          const jobRes = await axios.get(`${API}/jobs/${reuseJobId}/wizard-data`);
          const d = jobRes.data?.input_data || {};
          applyInputData(d);
          toast.success("Einstellungen vom vorherigen Auftrag geladen!");
        } catch (e) { /* ignore */ }
      }
      
      // Reuse from template
      const tplId = searchParams.get("template");
      if (tplId) {
        try {
          const tplRes = await axios.get(`${API}/thumbnail-templates/${tplId}`);
          const d = tplRes.data?.input_data || {};
          applyInputData(d);
          setSelectedTemplateId(tplId);
          toast.success(`Vorlage "${tplRes.data?.name}" geladen!`);
        } catch (e) { /* ignore */ }
      }
    };
    
    const applyInputData = (d) => {
      if (d.video_title) { setSelectedTitle(d.video_title); setCustomTitle(d.video_title); }
      if (d.description) setVideoDescription(d.description);
      if (d.style_id) setSelectedStyleId(d.style_id);
      if (d.brand_colors?.length) setBrandColors(d.brand_colors);
      if (d.tone_case) setSelectedToneCase(d.tone_case);
      if (d.variations) setVariationsCount(d.variations);
      if (d.selected_persons?.length) setSelectedPersons(d.selected_persons);
      if (d.selected_logos?.length) setSelectedLogos(d.selected_logos);
      if (d.selected_brand_slugs?.length) setSelectedBrandSlugs(d.selected_brand_slugs);
      if (d.logo_position) setLogoPosition(d.logo_position);
      if (d.thumbnail_format) setThumbnailFormat(d.thumbnail_format);
      if (d.image_instructions) setImageInstructions(d.image_instructions);
    };
    loadData();
  }, []);
  
  // Step 1: Start session
  const handleStartSession = async () => {
    if (!videoDescription.trim() && !videoUrl.trim()) {
      toast.error('Bitte beschreibe dein Video oder gib eine URL ein');
      return;
    }
    
    setIsLoading(true);
    try {
      // Use longer timeout (90s) for session start - Instagram transcription can take time
      const response = await axios.post(`${API}/generator/start-session`, {
        video_description: videoDescription || `Video von URL: ${videoUrl}`,
        video_url: videoUrl || null
      }, { timeout: 90000 });
      
      setSessionId(response.data.session_id);
      setQuestions(response.data.questions || []);
      setTitleSuggestions(response.data.title_suggestions || []);
      setVideoMetadata(response.data.video_metadata);
      
      // Initialize answers
      const initialAnswers = {};
      (response.data.questions || []).forEach(q => {
        initialAnswers[q.id] = '';
      });
      setAnswers(initialAnswers);
      
      // Skip to appropriate step
      if (response.data.status === 'titles_ready') {
        setCurrentStep(2); // Skip to title selection
      } else {
        setCurrentStep(1); // Go to questions
      }
      
      toast.success('Session gestartet!');
    } catch (err) {
      if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        toast.error('Timeout: Video-Analyse dauert zu lange. Bitte versuche es erneut oder nutze eine kürzere Beschreibung.');
      } else {
        toast.error('Fehler beim Starten der Session');
      }
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Step 2: Submit answers
  const handleSubmitAnswers = async () => {
    // Check if all questions are answered
    const unanswered = questions.filter(q => !answers[q.id]?.trim());
    if (unanswered.length > 0) {
      toast.error('Bitte beantworte alle Fragen');
      return;
    }
    
    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/generator/answer-questions`, {
        session_id: sessionId,
        answers: answers
      });
      
      setTitleSuggestions(response.data.title_suggestions || []);
      setCurrentStep(2);
      toast.success('Titel generiert!');
    } catch (err) {
      toast.error('Fehler beim Generieren der Titel');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Step 3: Regenerate titles with different tone
  const handleRegenerateTitles = async (newTone) => {
    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/generator/regenerate-titles`, {
        session_id: sessionId,
        tone_case: newTone
      });
      
      setTitleSuggestions(response.data.title_suggestions || []);
      setSelectedToneCase(newTone);
      toast.success('Neue Titel generiert!');
    } catch (err) {
      toast.error('Fehler beim Regenerieren');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Step 8: Generate thumbnail
  const handleGenerateThumbnail = async () => {
    if (!selectedTitle) {
      toast.error('Bitte wähle einen Titel aus');
      return;
    }
    
    setIsLoading(true);
    try {
      // First configure the session
      const configPayload = {
        session_id: sessionId,
        selected_title: selectedTitle,
        style_id: selectedStyleId || null,
        brand_colors: brandColors,
        tone_case: selectedToneCase,
        selected_persons: selectedPersons,
        selected_logos: selectedLogos,
        selected_brand_slugs: selectedBrandSlugs,
        logo_position: logoPosition,
        variations: variationsCount,
        thumbnail_format: thumbnailFormat,
        image_instructions: imageInstructions || null
      };
      
      await axios.post(`${API}/generator/select-title`, configPayload);
      
      // Then generate (always in background mode)
      const response = await axios.post(`${API}/generator/generate-thumbnail`, {
        session_id: sessionId,
        use_background: true
      });
      
      toast.success('Thumbnail-Generierung gestartet!');
      
      // Navigate to jobs page with the new job ID as URL parameter
      if (response.data.job_id) {
        navigate(`/jobs?new=${response.data.job_id}`);
      }
    } catch (err) {
      toast.error('Fehler bei der Generierung');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Brand color handlers
  const handleAddBrandColor = () => {
    const color = newBrandColor.trim();
    if (color && /^#[0-9A-Fa-f]{6}$/.test(color) && !brandColors.includes(color) && brandColors.length < 5) {
      setBrandColors(prev => [...prev, color]);
      setNewBrandColor('#');
    } else if (brandColors.length >= 5) {
      toast.error('Maximal 5 Farben erlaubt');
    } else if (color !== '#') {
      toast.error('Bitte gib eine gültige HEX-Farbe ein');
    }
  };
  
  const handleRemoveBrandColor = (color) => {
    setBrandColors(prev => prev.filter(c => c !== color));
  };
  
  // Person toggle
  const togglePersonSelection = (personId) => {
    setSelectedPersons(prev => 
      prev.includes(personId) 
        ? prev.filter(id => id !== personId)
        : [...prev, personId]
    );
  };
  
  // Navigation
  const canGoNext = () => {
    switch (currentStep) {
      case 0: return videoDescription.trim() || videoUrl.trim();
      case 1: return questions.every(q => answers[q.id]?.trim());
      case 2: return selectedTitle;
      case 3: return true; // Style is optional
      case 4: return true; // Colors are optional
      case 5: return true; // Tone is optional
      case 6: return true; // Persons are optional
      case 7: return true; // Logos are optional
      case 8: return selectedTitle; // Final step - need a title to generate
      default: return false;
    }
  };
  
  const handleNext = async () => {
    if (currentStep === 0) {
      await handleStartSession();
    } else if (currentStep === 1 && questions.length > 0) {
      await handleSubmitAnswers();
    } else if (currentStep === 8) {
      // Step 9 (Generieren) - actually generate the thumbnail
      await handleGenerateThumbnail();
    } else {
      setCurrentStep(prev => Math.min(prev + 1, steps.length - 1));
    }
  };
  
  const handleBack = () => {
    setCurrentStep(prev => Math.max(prev - 1, 0));
  };
  
  // Render step content
  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <div className="space-y-6">
            <Tabs value={generatorMode} onValueChange={setGeneratorMode}>
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="manual" data-testid="tab-manual">
                  <MessageSquare className="w-4 h-4 mr-2" />
                  Freies Schreiben
                </TabsTrigger>
                <TabsTrigger value="video-url" data-testid="tab-video-url">
                  <LinkIcon className="w-4 h-4 mr-2" />
                  Video-URL
                </TabsTrigger>
              </TabsList>
              
              <TabsContent value="manual" className="mt-6">
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="video-description" className="text-base font-medium">
                      Worum geht es in deinem Video?
                    </Label>
                    <p className="text-sm text-muted-foreground mb-3">
                      Beschreibe kurz den Inhalt deines Videos. Je mehr Details, desto besser die Titel-Vorschläge.
                    </p>
                    <Textarea
                      id="video-description"
                      data-testid="video-description-input"
                      placeholder="z.B. Recruiting Film für unseren Kunden RAN Gruppe, ein Bauunternehmen. Wir zeigen die Unternehmenskultur und interviewen Mitarbeiter..."
                      value={videoDescription}
                      onChange={(e) => setVideoDescription(e.target.value)}
                      className="min-h-[150px]"
                    />
                  </div>
                </div>
              </TabsContent>
              
              <TabsContent value="video-url" className="mt-6">
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="video-url" className="text-base font-medium">
                      Video-URL eingeben
                    </Label>
                    <p className="text-sm text-muted-foreground mb-3">
                      YouTube oder Instagram URL. Metadaten werden automatisch extrahiert.
                    </p>
                    <Input
                      id="video-url"
                      data-testid="video-url-input"
                      placeholder="https://youtube.com/watch?v=..."
                      value={videoUrl}
                      onChange={(e) => setVideoUrl(e.target.value)}
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="video-description-url" className="text-base font-medium">
                      Zusätzliche Beschreibung (optional)
                    </Label>
                    <Textarea
                      id="video-description-url"
                      placeholder="Weitere Details zum Video..."
                      value={videoDescription}
                      onChange={(e) => setVideoDescription(e.target.value)}
                      className="min-h-[100px]"
                    />
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </div>
        );
        
      case 1:
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <HelpCircle className="w-12 h-12 mx-auto text-primary mb-3" />
              <h3 className="text-lg font-medium">Ein paar Fragen für den perfekten Titel</h3>
              <p className="text-sm text-muted-foreground">
                Deine Antworten helfen uns, Scroll-Stopper Titel zu erstellen.
              </p>
            </div>
            
            {questions.length === 0 ? (
              <div className="text-center py-8">
                <Loader2 className="w-8 h-8 animate-spin mx-auto text-muted-foreground" />
                <p className="text-sm text-muted-foreground mt-2">Keine Fragen nötig...</p>
              </div>
            ) : (
              <div className="space-y-6">
                {questions.map((q, index) => (
                  <div key={q.id} className="p-4 rounded-lg border bg-muted/30">
                    <Label className="text-base font-medium flex items-center gap-2 mb-3">
                      <span className="bg-primary text-primary-foreground w-6 h-6 rounded-full flex items-center justify-center text-sm">
                        {index + 1}
                      </span>
                      {q.question}
                    </Label>
                    
                    {/* Radio buttons for suggestions */}
                    {q.question_type === 'single_choice' && q.options && (
                      <RadioGroup
                        value={answers[q.id]?.startsWith('__custom__') ? '' : (answers[q.id] || '')}
                        onValueChange={(value) => setAnswers(prev => ({...prev, [q.id]: value}))}
                        className="grid grid-cols-2 gap-2 mb-3"
                      >
                        {q.options.map((option, optIndex) => (
                          <div key={optIndex} className="flex items-center space-x-2">
                            <RadioGroupItem value={option} id={`${q.id}-${optIndex}`} />
                            <Label htmlFor={`${q.id}-${optIndex}`} className="cursor-pointer text-sm">
                              {option}
                            </Label>
                          </div>
                        ))}
                      </RadioGroup>
                    )}
                    
                    {/* Always show free text input */}
                    <div className="mt-3">
                      <Label className="text-xs text-muted-foreground mb-1 block">
                        {q.question_type === 'single_choice' ? 'Oder eigene Antwort:' : 'Deine Antwort:'}
                      </Label>
                      <Input
                        placeholder="Eigene Antwort eingeben..."
                        value={answers[q.id]?.startsWith('__custom__') ? answers[q.id].replace('__custom__', '') : (q.question_type !== 'single_choice' ? (answers[q.id] || '') : '')}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (q.question_type === 'single_choice') {
                            setAnswers(prev => ({...prev, [q.id]: val ? `__custom__${val}` : ''}));
                          } else {
                            setAnswers(prev => ({...prev, [q.id]: val}));
                          }
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
        
      case 2:
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <Sparkles className="w-12 h-12 mx-auto text-primary mb-3" />
              <h3 className="text-lg font-medium">Wähle deinen Scroll-Stopper Titel</h3>
              <p className="text-sm text-muted-foreground">
                Diese Titel wurden speziell für dein Video erstellt.
              </p>
            </div>
            
            {titleSuggestions.length === 0 ? (
              <div className="text-center py-8">
                <Loader2 className="w-8 h-8 animate-spin mx-auto text-muted-foreground" />
                <p className="text-sm text-muted-foreground mt-2">Titel werden generiert...</p>
              </div>
            ) : (
              <>
                {/* Manual title input */}
                <div className="mb-4 p-4 rounded-lg border-2 border-dashed border-muted hover:border-primary/50 transition-all">
                  <Label className="text-sm font-medium mb-2 block">Eigenen Titel eingeben:</Label>
                  <div className="flex gap-2">
                    <Input
                      placeholder="Dein eigener Scroll-Stopper Titel..."
                      value={customTitle}
                      onChange={(e) => setCustomTitle(e.target.value)}
                      className="flex-1"
                    />
                    <Button
                      variant={selectedTitle === customTitle && customTitle ? "default" : "outline"}
                      size="sm"
                      onClick={() => {
                        if (customTitle.trim()) {
                          setSelectedTitle(customTitle.trim());
                        }
                      }}
                      disabled={!customTitle.trim()}
                    >
                      <Check className="w-4 h-4 mr-1" />
                      Auswählen
                    </Button>
                  </div>
                  {selectedTitle === customTitle && customTitle && (
                    <p className="text-xs text-primary mt-2 flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3" />
                      Dein eigener Titel ist ausgewählt
                    </p>
                  )}
                </div>

                <div className="relative my-4">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-background px-2 text-muted-foreground">oder KI-Vorschläge</span>
                  </div>
                </div>

                <RadioGroup
                  value={selectedTitle}
                  onValueChange={setSelectedTitle}
                  className="space-y-3"
                >
                  {titleSuggestions.map((t, index) => (
                    <div
                      key={index}
                      className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                        selectedTitle === t.title 
                          ? 'border-primary bg-primary/5' 
                          : 'border-muted hover:border-primary/50'
                      }`}
                      onClick={() => setSelectedTitle(t.title)}
                    >
                      <div className="flex items-start gap-3">
                        <RadioGroupItem value={t.title} id={`title-${index}`} className="mt-1" />
                        <div className="flex-1">
                          <Label htmlFor={`title-${index}`} className="text-base font-medium cursor-pointer block">
                            {t.title}
                          </Label>
                          {t.reasoning && (
                            <p className="text-sm text-muted-foreground mt-1">{t.reasoning}</p>
                          )}
                        </div>
                        {selectedTitle === t.title && (
                          <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0" />
                        )}
                      </div>
                    </div>
                  ))}
                </RadioGroup>
              </>
            )}
            
            <div className="flex items-center gap-2 pt-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleRegenerateTitles(selectedToneCase)}
                disabled={isLoading}
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
                Neue Titel
              </Button>
              
              <Select value={selectedToneCase} onValueChange={(v) => handleRegenerateTitles(v)}>
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="Ton wählen..." />
                </SelectTrigger>
                <SelectContent>
                  {toneCases.map((tc) => (
                    <SelectItem key={tc.value} value={tc.value}>
                      {tc.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        );
        
      case 3:
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <Palette className="w-12 h-12 mx-auto text-primary mb-3" />
              <h3 className="text-lg font-medium">Thumbnail-Stil wählen</h3>
              <p className="text-sm text-muted-foreground">
                Wähle einen analysierten Stil als Vorlage (optional).
              </p>
            </div>
            
            <div className="space-y-3">
              <div
                className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                  !selectedStyleId ? 'border-primary bg-primary/5' : 'border-muted hover:border-primary/50'
                }`}
                onClick={() => setSelectedStyleId('')}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    !selectedStyleId ? 'border-primary' : 'border-muted-foreground'
                  }`}>
                    {!selectedStyleId && <div className="w-2.5 h-2.5 rounded-full bg-primary" />}
                  </div>
                  <div>
                    <span className="font-medium">Kein spezifischer Stil</span>
                    <p className="text-sm text-muted-foreground">KI erstellt optimalen Scroll-Stopper</p>
                  </div>
                </div>
              </div>
              
              {styles.map((style) => (
                <div
                  key={style.id}
                  className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                    selectedStyleId === style.id ? 'border-primary bg-primary/5' : 'border-muted hover:border-primary/50'
                  }`}
                  onClick={() => setSelectedStyleId(style.id)}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                      selectedStyleId === style.id ? 'border-primary' : 'border-muted-foreground'
                    }`}>
                      {selectedStyleId === style.id && <div className="w-2.5 h-2.5 rounded-full bg-primary" />}
                    </div>
                    <div className="flex-1">
                      <span className="font-medium">{style.name}</span>
                      <div className="flex gap-1 mt-1">
                        {style.aggregated_style?.dominant_palette?.slice(0, 5).map((color, i) => (
                          <div
                            key={i}
                            className="w-4 h-4 rounded-sm border"
                            style={{ backgroundColor: color.hex }}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
        
      case 4:
        // Get colors from selected color source style (or thumbnail style as fallback)
        const colorSourceStyle = colorSourceStyleId 
          ? styles.find(s => s.id === colorSourceStyleId)
          : styles.find(s => s.id === selectedStyleId);
        const styleColors = colorSourceStyle?.aggregated_style?.dominant_palette || [];
        
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <Palette className="w-12 h-12 mx-auto text-primary mb-3" />
              <h3 className="text-lg font-medium">Farben wählen</h3>
              <p className="text-sm text-muted-foreground">
                Wähle Farben aus einem Style oder füge eigene CI-Farben hinzu.
              </p>
            </div>
            
            {/* Style dropdown for color source */}
            <div className="space-y-3">
              <Label className="text-sm font-medium">
                Farben aus Style übernehmen:
              </Label>
              <Select 
                value={colorSourceStyleId || selectedStyleId || ''} 
                onValueChange={(value) => {
                  setColorSourceStyleId(value);
                  // Auto-add first 3 colors from selected style
                  const selectedStyle = styles.find(s => s.id === value);
                  if (selectedStyle?.aggregated_style?.dominant_palette) {
                    const newColors = selectedStyle.aggregated_style.dominant_palette
                      .slice(0, 3)
                      .map(c => c.hex || c)
                      .filter(hex => hex && /^#[0-9A-Fa-f]{6}$/i.test(hex));
                    setBrandColors(newColors);
                    if (newColors.length > 0) {
                      toast.success(`${newColors.length} Farben von "${selectedStyle.name}" hinzugefügt`);
                    }
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Style für Farben wählen..." />
                </SelectTrigger>
                <SelectContent>
                  {styles.map((style) => (
                    <SelectItem key={style.id} value={style.id}>
                      <div className="flex items-center gap-2">
                        <span>{style.name}</span>
                        <div className="flex gap-0.5">
                          {style.aggregated_style?.dominant_palette?.slice(0, 3).map((c, i) => (
                            <div 
                              key={i} 
                              className="w-3 h-3 rounded-sm border" 
                              style={{ backgroundColor: c.hex || c }} 
                            />
                          ))}
                        </div>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Style colors section */}
            {styleColors.length > 0 && (
              <div className="space-y-3">
                <Label className="text-sm font-medium">
                  Farben aus "{colorSourceStyle?.name}" zum Hinzufügen:
                </Label>
                <div className="flex flex-wrap gap-2">
                  {styleColors.slice(0, 8).map((color, index) => {
                    const colorHex = color.hex || color;
                    const isSelected = brandColors.includes(colorHex);
                    return (
                      <button
                        key={index}
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg border-2 transition-all ${
                          isSelected 
                            ? 'border-primary bg-primary/10' 
                            : 'border-muted hover:border-primary/50'
                        }`}
                        onClick={() => {
                          if (isSelected) {
                            handleRemoveBrandColor(colorHex);
                          } else if (brandColors.length < 5) {
                            setBrandColors(prev => [...prev, colorHex]);
                          } else {
                            toast.error('Maximal 5 Farben erlaubt');
                          }
                        }}
                      >
                        <div 
                          className="w-6 h-6 rounded border" 
                          style={{ backgroundColor: colorHex }} 
                        />
                        <span className="text-xs font-mono">{colorHex}</span>
                        {isSelected && <Check className="w-4 h-4 text-primary" />}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            
            {/* Manual color input */}
            <div className="space-y-3">
              <Label className="text-sm font-medium">
                Eigene CI-Farbe hinzufügen:
              </Label>
              <div className="flex gap-2">
                <Input
                  placeholder="#FF5500"
                  value={newBrandColor}
                  onChange={(e) => setNewBrandColor(e.target.value)}
                  className="flex-1"
                />
                <div
                  className="w-10 h-10 rounded border"
                  style={{ backgroundColor: /^#[0-9A-Fa-f]{6}$/.test(newBrandColor) ? newBrandColor : '#fff' }}
                />
                <Button onClick={handleAddBrandColor} size="icon">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>
            
            {/* Selected colors */}
            {brandColors.length > 0 && (
              <div className="space-y-2">
                <Label className="text-sm font-medium">Ausgewählte Farben:</Label>
                <div className="flex flex-wrap gap-2">
                  {brandColors.map((color, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-2 px-3 py-1.5 rounded-full border bg-muted/50"
                    >
                      <div className="w-4 h-4 rounded-full border" style={{ backgroundColor: color }} />
                      <span className="text-sm font-mono">{color}</span>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-5 w-5"
                        onClick={() => handleRemoveBrandColor(color)}
                      >
                        <X className="w-3 h-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            <p className="text-xs text-muted-foreground">
              {brandColors.length}/5 Farben ausgewählt
            </p>
          </div>
        );
        
      case 5:
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <Zap className="w-12 h-12 mx-auto text-primary mb-3" />
              <h3 className="text-lg font-medium">Ton / Stil wählen</h3>
              <p className="text-sm text-muted-foreground">
                Wie soll dein Thumbnail wirken?
              </p>
            </div>
            
            <RadioGroup
              value={selectedToneCase}
              onValueChange={setSelectedToneCase}
              className="space-y-3"
            >
              {toneCases.map((tc) => (
                <div
                  key={tc.value}
                  className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                    selectedToneCase === tc.value ? 'border-primary bg-primary/5' : 'border-muted hover:border-primary/50'
                  }`}
                  onClick={() => setSelectedToneCase(tc.value)}
                >
                  <div className="flex items-center gap-3">
                    <RadioGroupItem value={tc.value} id={tc.value} />
                    <div>
                      <Label htmlFor={tc.value} className="font-medium cursor-pointer">{tc.label}</Label>
                      <p className="text-sm text-muted-foreground">{tc.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </RadioGroup>
          </div>
        );
        
      case 6:
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <Users className="w-12 h-12 mx-auto text-primary mb-3" />
              <h3 className="text-lg font-medium">Personen hinzufügen</h3>
              <p className="text-sm text-muted-foreground">
                Wähle Personen aus deiner Bibliothek (optional).
              </p>
            </div>
            
            {persons.length === 0 ? (
              <div className="text-center py-8 border rounded-lg bg-muted/30">
                <Users className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
                <p className="text-sm text-muted-foreground mb-3">Noch keine Personen vorhanden</p>
                <Button variant="outline" size="sm" asChild>
                  <Link to="/persons">
                    <Plus className="w-4 h-4 mr-2" />
                    Personen hinzufügen
                  </Link>
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {persons.map((person) => (
                  <div
                    key={person.id}
                    className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                      selectedPersons.includes(person.id) 
                        ? 'border-primary bg-primary/5' 
                        : 'border-muted hover:border-primary/50'
                    }`}
                    onClick={() => togglePersonSelection(person.id)}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-muted overflow-hidden">
                        <img
                          src={`${API}/persons/${person.id}/image?token=${localStorage.getItem('auth_token')}`}
                          alt={person.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{person.name}</p>
                        {person.role && (
                          <p className="text-xs text-muted-foreground truncate">{person.role}</p>
                        )}
                      </div>
                      {selectedPersons.includes(person.id) && (
                        <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0" />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            {selectedPersons.length > 0 && (
              <p className="text-sm text-muted-foreground">
                {selectedPersons.length} Person(en) ausgewählt
              </p>
            )}
          </div>
        );
        
      case 7:
        // Logo selection step
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <svg className="w-12 h-12 mx-auto text-primary mb-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="3" width="18" height="18" rx="2" />
                <path d="M9 12h6" />
                <path d="M12 9v6" />
              </svg>
              <h3 className="text-lg font-medium">Logos hinzufügen</h3>
              <p className="text-sm text-muted-foreground">
                Wähle Kunden-Logos oder bekannte Marken (Claude, ChatGPT, GitHub...) für das Thumbnail.
              </p>
            </div>

            {/* Brand library section — curated logos for AI/Dev/Social tools */}
            <BrandLibrarySection
              selectedSlugs={selectedBrandSlugs}
              onChange={setSelectedBrandSlugs}
              onOpenPicker={() => setBrandPickerOpen(true)}
              videoDescription={videoDescription}
              dismissed={dismissedBrandSuggestion}
              onDismissSuggestion={() => setDismissedBrandSuggestion(true)}
            />

            <div className="pt-2">
              <h4 className="text-sm font-semibold mb-2 text-muted-foreground">Eigene Logos</h4>
            </div>

            {logos.length === 0 ? (
              <div className="text-center py-8 border rounded-lg bg-muted/30">
                <svg className="w-8 h-8 mx-auto text-muted-foreground mb-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="3" width="18" height="18" rx="2" />
                  <path d="M9 12h6" />
                  <path d="M12 9v6" />
                </svg>
                <p className="text-sm text-muted-foreground mb-3">Noch keine Logos vorhanden</p>
                <Button variant="outline" size="sm" asChild>
                  <Link to="/logos">
                    <Plus className="w-4 h-4 mr-2" />
                    Logo hochladen
                  </Link>
                </Button>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {logos.map((logo) => (
                    <div
                      key={logo.id}
                      className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                        selectedLogos.includes(logo.id) 
                          ? 'border-primary bg-primary/5' 
                          : 'border-muted hover:border-primary/50'
                      }`}
                      onClick={() => {
                        if (selectedLogos.includes(logo.id)) {
                          setSelectedLogos(selectedLogos.filter(id => id !== logo.id));
                        } else {
                          setSelectedLogos([...selectedLogos, logo.id]);
                        }
                      }}
                      data-testid={`logo-select-${logo.id}`}
                    >
                      <div className="aspect-square bg-muted/30 rounded-md mb-2 flex items-center justify-center p-2">
                        <img
                          src={`${BACKEND_URL}${logo.image_url}?token=${localStorage.getItem('auth_token')}`}
                          alt={logo.name}
                          className="max-w-full max-h-full object-contain"
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <p className="font-medium text-xs truncate flex-1">{logo.name}</p>
                        {selectedLogos.includes(logo.id) && (
                          <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0 ml-1" />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Logo Position Selection */}
                {selectedLogos.length > 0 && (
                  <div className="space-y-3 pt-4 border-t">
                    <Label className="text-sm font-medium">Logo-Positionierung</Label>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                      {[
                        { value: 'ai_decides', label: '🤖 KI entscheidet', desc: 'Optimale Position' },
                        { value: 'top-left', label: '↖️ Oben links', desc: '' },
                        { value: 'top-right', label: '↗️ Oben rechts', desc: '' },
                        { value: 'bottom-left', label: '↙️ Unten links', desc: '' },
                        { value: 'bottom-right', label: '↘️ Unten rechts', desc: '' },
                        { value: 'center', label: '🎯 Zentriert', desc: '' }
                      ].map((pos) => (
                        <Button
                          key={pos.value}
                          variant={logoPosition === pos.value ? 'default' : 'outline'}
                          size="sm"
                          className="justify-start h-auto py-2"
                          onClick={() => setLogoPosition(pos.value)}
                        >
                          <span className="text-xs">{pos.label}</span>
                        </Button>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
            
            {selectedLogos.length > 0 && (
              <p className="text-sm text-muted-foreground">
                {selectedLogos.length} Logo(s) ausgewählt • Position: {
                  logoPosition === 'ai_decides' ? 'KI entscheidet' :
                  logoPosition === 'top-left' ? 'Oben links' :
                  logoPosition === 'top-right' ? 'Oben rechts' :
                  logoPosition === 'bottom-left' ? 'Unten links' :
                  logoPosition === 'bottom-right' ? 'Unten rechts' : 'Zentriert'
                }
              </p>
            )}
          </div>
        );
        
      case 8:
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <Wand2 className="w-12 h-12 mx-auto text-primary mb-3" />
              <h3 className="text-lg font-medium">Thumbnail generieren</h3>
              <p className="text-sm text-muted-foreground">
                Überprüfe deine Auswahl und starte die Generierung.
              </p>
            </div>
            
            {/* Summary */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Zusammenfassung</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Titel:</span>
                  <span className="font-medium text-right max-w-[60%] truncate">{selectedTitle}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Style:</span>
                  <span>{selectedStyleId ? styles.find(s => s.id === selectedStyleId)?.name : 'KI-optimiert'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Ton:</span>
                  <span>{toneCases.find(tc => tc.value === selectedToneCase)?.label}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">CI-Farben:</span>
                  <span>{brandColors.length > 0 ? brandColors.length : 'Keine'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Personen:</span>
                  <span>{selectedPersons.length > 0 ? selectedPersons.length : 'Keine'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Logos:</span>
                  <span>{selectedLogos.length > 0 ? `${selectedLogos.length} (${logoPosition === 'ai_decides' ? 'KI' : logoPosition})` : 'Keine'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Format:</span>
                  <span>{thumbnailFormat === 'superchat' ? 'Superchat (1125×600)' : 'YouTube (1280×720)'}</span>
                </div>
              </CardContent>
            </Card>
            
            {/* Image Instructions (optional) */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Bildanweisungen (optional)</Label>
              <Textarea
                value={imageInstructions}
                onChange={(e) => setImageInstructions(e.target.value)}
                placeholder="z.B. Person soll lächeln und in die Kamera schauen, dunkler Hintergrund, großer Text..."
                className="min-h-[80px]"
                data-testid="image-instructions"
              />
              <p className="text-xs text-muted-foreground">
                Beschreibe optional, wie das Thumbnail aussehen soll. Die KI berücksichtigt diese Anweisungen bei der Generierung.
              </p>
            </div>

            {/* Format Selection */}
            <div className="space-y-3">
              <Label className="text-sm font-medium">Format</Label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setThumbnailFormat('youtube')}
                  className={`flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-colors ${thumbnailFormat === 'youtube' ? 'border-primary bg-primary/5' : 'border-muted hover:border-muted-foreground/30'}`}
                  data-testid="format-youtube"
                >
                  <div className="w-full aspect-video bg-muted rounded" style={{maxWidth: '120px'}} />
                  <span className="text-sm font-medium">YouTube</span>
                  <span className="text-xs text-muted-foreground">1280 × 720 px</span>
                </button>
                <button
                  type="button"
                  onClick={() => setThumbnailFormat('superchat')}
                  className={`flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-colors ${thumbnailFormat === 'superchat' ? 'border-primary bg-primary/5' : 'border-muted hover:border-muted-foreground/30'}`}
                  data-testid="format-superchat"
                >
                  <div className="w-full bg-muted rounded" style={{maxWidth: '120px', aspectRatio: '1125/600'}} />
                  <span className="text-sm font-medium">Superchat</span>
                  <span className="text-xs text-muted-foreground">1125 × 600 px</span>
                </button>
              </div>
            </div>
            
            {/* Variations Selection */}
            <div className="space-y-3">
              <Label className="text-sm font-medium">Anzahl Varianten</Label>
              <div className="grid grid-cols-4 gap-2">
                {[1, 2, 3, 4].map((num) => (
                  <Button
                    key={num}
                    type="button"
                    variant={variationsCount === num ? "default" : "outline"}
                    className="h-12"
                    onClick={() => setVariationsCount(num)}
                  >
                    {num}
                  </Button>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">
                Mehr Varianten = mehr Auswahl, aber längere Generierungszeit
              </p>
            </div>
            
            {/* Info about background processing */}
            <div className="flex items-center gap-3 p-4 rounded-lg border bg-muted/30">
              <Loader2 className="w-5 h-5 text-primary" />
              <p className="text-sm text-muted-foreground">
                Die Generierung läuft im Hintergrund. Du kannst die Seite verlassen und den Fortschritt unter "Meine Aufträge" verfolgen.
              </p>
            </div>
          </div>
        );
        
      default:
        return null;
    }
  };
  
  return (
    <div className="space-y-6" data-testid="generator-2-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-2 rounded-lg bg-primary/10">
          <Sparkles className="w-6 h-6 text-primary" />
        </div>
        <div>
          <h1 className="text-2xl font-bold">Thumbnail Generator 2.0</h1>
          <p className="text-muted-foreground">
            Scroll-Stopper Thumbnails mit KI-optimierten Titeln
          </p>
        </div>
      </div>
      
      {/* Saved Templates */}
      {thumbnailTemplates.length > 0 && currentStep === 0 && (
        <div className="space-y-2">
          <Label className="text-sm font-medium text-muted-foreground">Gespeicherte Vorlagen</Label>
          <div className="flex gap-2 overflow-x-auto pb-2">
            {thumbnailTemplates.map((tpl) => (
              <button
                key={tpl.id}
                onClick={() => {
                  navigate(`/thumbnails?template=${tpl.id}`);
                  window.location.reload();
                }}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm whitespace-nowrap transition-colors hover:bg-muted/50 ${selectedTemplateId === tpl.id ? 'border-primary bg-primary/5' : 'border-muted'}`}
                data-testid={`thumb-template-${tpl.id}`}
              >
                <BookMarked className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                <span>{tpl.name}</span>
                <span className="text-xs text-muted-foreground">{tpl.thumbnail_format === 'superchat' ? 'SC' : 'YT'}</span>
              </button>
            ))}
          </div>
        </div>
      )}
      
      {/* Step Indicator */}
      <StepIndicator currentStep={currentStep} totalSteps={steps.length} steps={steps} />
      
      {/* Content Card */}
      <Card className="max-w-2xl mx-auto">
        <CardContent className="pt-6">
          {renderStepContent()}
        </CardContent>
      </Card>
      
      {/* Navigation - Fixed at bottom on mobile with safe area support */}
      <div 
        className="flex justify-between max-w-2xl mx-auto gap-3 sticky bottom-0 bg-background pt-4 md:static md:bg-transparent md:pt-0 border-t md:border-t-0 -mx-4 px-4 md:mx-0 md:px-0 pb-8 md:pb-0"
        style={{ paddingBottom: 'calc(2rem + env(safe-area-inset-bottom, 0px))' }}
      >
        <Button
          variant="outline"
          onClick={handleBack}
          disabled={currentStep === 0 || isLoading}
          className="flex-1 md:flex-none h-12 md:h-10"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Zurück
        </Button>
        
        <Button
          onClick={handleNext}
          disabled={!canGoNext() || isLoading}
          data-testid="next-step-btn"
          className="flex-1 md:flex-none h-12 md:h-10"
        >
          {isLoading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Lädt...
            </>
          ) : currentStep === steps.length - 1 ? (
            <>
              <Wand2 className="w-4 h-4 mr-2" />
              Generieren
            </>
          ) : (
            <>
              Weiter
              <ArrowRight className="w-4 h-4 ml-2" />
            </>
          )}
        </Button>
      </div>

      {/* Brand Logo Picker (curated AI/Dev/Social logos) */}
      <BrandLogoPickerDialog
        open={brandPickerOpen}
        onOpenChange={setBrandPickerOpen}
        selectedSlugs={selectedBrandSlugs}
        onChange={setSelectedBrandSlugs}
        maxSelection={4}
      />
    </div>
  );
};

export default Generator2Page;
